import { useEffect, useRef, useState } from 'react';
import { useApi } from '../context';

/**
 * Upload / preview / clear a single audio prompt. The parent owns the blob name (a
 * `uuid.wav` returned by POST /api/admin/audio); this component never mutates a line
 * directly. Any common audio file is accepted — the API transcodes it to the Teams WAV
 * format with ffmpeg, so callers just pick a file.
 */
export function AudioField({
  label,
  blob,
  onChange,
}: {
  label: string;
  blob: string | null;
  onChange: (blob: string | null) => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const setPreviewUrl = (url: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setPlayUrl(url);
  };
  // Revoke the last object URL when the component unmounts.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.postRaw<{ blob: string }>('/api/admin/audio', file, file.type || 'application/octet-stream');
      onChange(res.blob);
      setPreviewUrl(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function preview() {
    if (!blob) return;
    setError(null);
    try {
      const b = await api.blob(`/api/admin/audio/${encodeURIComponent(blob)}`);
      setPreviewUrl(URL.createObjectURL(b));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="audio-field">
      <div className="filters">
        <span className="sub">{label}:</span>
        {blob ? <span className="pill ok">uploaded</span> : <span className="sub">none</span>}
        <label className="link">
          {blob ? 'replace' : 'upload'}
          <input
            type="file"
            accept="audio/*,video/mp4,.m4a,.mp3,.wav"
            style={{ display: 'none' }}
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
              e.target.value = '';
            }}
          />
        </label>
        {blob && <button type="button" className="link" onClick={preview}>play</button>}
        {blob && <button type="button" className="link danger" onClick={() => { onChange(null); setPreviewUrl(null); }}>clear</button>}
        {busy && <span className="sub">converting…</span>}
      </div>
      {error && <p className="error">{error}</p>}
      {playUrl && <audio src={playUrl} controls autoPlay style={{ height: '2rem', marginTop: '0.25rem' }} />}
    </div>
  );
}
